// ---------------------------------------------------------------------------
// The chat window. One stream, with global, team and spectator lines in it.
//
// The load-bearing part is not the message list, it is the composer. During a
// round every keystroke belongs to the answer, so chat must never quietly
// acquire the keyboard: while the mode is 'answer' there is no text field in the
// DOM at all, only a dashed field that says how to take chat. A player who loses
// a round's typing into a chat box does not come back.
//
// Focus is owned by `useKeyboardMode`, shared with the screen. Both halves have
// to agree, which is why the api arrives as a prop rather than being created
// here: two instances of the hook would be two disagreeing answers to "who has
// the keyboard". Holding the keyboard is a fact the screen needs, not an
// announcement the player needs — nothing here narrates it.
//
// Reading is one stream; sending still needs a target, so the composer carries
// one. It is a control rather than a tab: it says where the next line goes and
// changes nothing about what is on screen. Sending a team line to the whole room
// by accident is the mistake that costs something in a game with hidden answers,
// so the target is on the field itself, in the team's own colour.
//
// Nothing in this panel may change size with its contents. The list is a
// scroller with a flex basis and no content-driven minimum, the header and the
// composer never shrink, every composer state is the same height as the others,
// and the scrollbar gutter is reserved whether or not there is one.
//
// `chatMode` has three settings and each of them lands somewhere different.
// `everyone` is what every room had. `players` takes the composer away from the
// spectator bench and leaves the stream — one more composer state, the same
// height as the rest. `off` takes the whole panel, which is this component
// returning null and the room shell dropping the drawer and the button on the
// same reading; the panel carries nothing but the messages, so with those gone
// there is nothing left for it to be. All three are host-changeable MID-GAME,
// so none of them may be decided at mount: `hidden` is read every render, and
// the effect beside it hands the keyboard back on the way out.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { EnterKeyIcon, Icon } from '../primitives/Icon.js';
import { CHAT_MESSAGE_MAX } from '@countdown/shared';
import type {
  ChatChannel,
  ChatMessage,
  ClientGameState,
  ClientMessage,
  GamePhase,
  PublicPlayer,
} from '@countdown/shared';
import type { KeyboardModeApi } from '../../hooks/useKeyboardMode.js';
import { canSendChat, chatPanelShown, readableBy } from './chatAccess.js';
import { ChatMessageRow, singleLine } from './ChatMessageRow.js';
import { ChatModerationMenu } from './ChatModerationMenu.js';
import { RoomRoster } from './RoomRoster.js';

// The room shell reads this to decide whether there is a drawer and a button at
// all, and it has imported it from here since before there was a `chatAccess`.
// Re-exported rather than moved so that one import does not become two.
export { readableBy } from './chatAccess.js';

export interface ChatPanelProps {
  state: ClientGameState;
  send: (message: ClientMessage) => void;
  /**
   * Shared with the screen, so the answer field and the chat box can never both
   * believe they own the keyboard. Required on purpose: a default would let a
   * screen forget to pass it and lose a round's typing.
   */
  keyboard: KeyboardModeApi;
  className?: string;
}

/**
 * Phases where a keystroke is worth something. The composer parks itself in all
 * of them, including the selection phases, where vowel and consonant calls are
 * on the keyboard too.
 */
const PARKED_PHASES: ReadonlySet<GamePhase> = new Set<GamePhase>([
  'letters-selection',
  'letters-play',
  'numbers-selection',
  'numbers-play',
  'conundrum-play',
]);

/**
 * Channel prefixes, per DESIGN.md section 10. Still honoured, deliberately not
 * advertised: the target control is the way to reach the team channel, and a
 * player should not have to learn a command language to use chat.
 *
 * Typing one moves the control rather than working behind it. Two mechanisms
 * that disagreed about where the next line was going would be worse than one.
 */
const PREFIX = /^\/(t|team|g|global)\s+/i;

/**
 * A prefix at the head of the draft, resolved against whether the player has a
 * team to send to at all. Null when there is no prefix, which is the ordinary
 * case: the target control decides.
 *
 * A prefix is always consumed, including `/t` from somebody with no team. The
 * server strips it too, so leaving it in the body would not have sent it as
 * words — it would have sent a team line with no team, which lands nowhere and
 * looks to the player like the message simply vanished.
 */
function prefixed(raw: string, onTeam: boolean): { channel: ChatChannel; body: string } | null {
  const match = PREFIX.exec(raw);
  if (match === null) return null;
  const wantsTeam = match[1].toLowerCase().startsWith('t');
  return { channel: wantsTeam && onTeam ? 'team' : 'global', body: raw.slice(match[0].length) };
}

/**
 * What the panel is showing above the composer.
 *
 * The people list is the host's moderation surface and it lives here rather than
 * on a play screen; the reasoning is written out in full in `RoomRoster.tsx`.
 * It replaces the message list rather than sitting beside it, because nothing in
 * this panel may change size with its contents and two stacked scrollers in a
 * 260px basis is two portholes.
 */
type ChatView = 'chat' | 'people';

/**
 * One of the host's two headings.
 *
 * A pressed heading rather than a tab strip with a `tablist` role: the two
 * panels below it are not a related set of documents, they are the log and the
 * roster, and `aria-pressed` says what a press does without promising arrow-key
 * navigation this does not implement. The type stays `label-mono` in both states
 * — the same the header always was — so the strip reads identically to the label
 * it replaces and switching it does not move the panel.
 *
 * **The 44px target is bought with negative margin rather than with header
 * height.** `-my-2` lets the button stand 44px tall while contributing 28 to the
 * row, which is under what the header's own padding and type already come to, so
 * the strip is exactly as tall for a host as for anybody else. Nothing in this
 * panel may change size, and "unless the reader is the host" is not an exception
 * worth having. Only the text colour changes on hover, so nothing is painted
 * over the rule underneath.
 */
function ViewTab({
  label,
  on,
  onSelect,
}: {
  label: string;
  on: boolean;
  onSelect: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onSelect}
      className={`label-mono -my-2 flex min-h-11 cursor-pointer items-center rounded-chip px-2 transition-colors duration-150 ease-out ${
        on ? 'text-ink' : 'text-ink-dim hover:text-ink-muted'
      }`}
    >
      {label}
    </button>
  );
}

export function ChatPanel({ state, send, keyboard, className }: ChatPanelProps): ReactElement | null {
  const [view, setView] = useState<ChatView>('chat');
  const [draft, setDraft] = useState('');
  // Which row's menu is open. One id, so opening a menu closes the last one
  // without either of them knowing about the other — the same structure the
  // roster and the lobby's seat list use, and for the same reason.
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  // Where the next line goes. Sticky, because a target that snapped back after
  // every message would make a conversation with your team a chore.
  const [target, setTarget] = useState<ChatChannel>('global');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLOListElement | null>(null);
  // The window follows the newest line only while the reader is already at the
  // bottom. Yanking someone out of the history they scrolled back to read is the
  // rudest thing a chat window can do.
  const pinnedRef = useRef(true);

  const you = state.players.find((player) => player.id === state.youId) ?? null;
  const yourTeamId = you?.teamId ?? null;
  const onTeam = yourTeamId !== null && state.teams.length > 0;
  const muted = you?.muted ?? false;

  const parked = PARKED_PHASES.has(state.phase);
  const focused = keyboard.mode === 'chat';

  // **The whole panel, or none of it.** `chatMode: 'off'` is a safety control
  // the host can reach mid-game, so this is a live transition and not a mount
  // decision: the room may be reading the panel at the moment it goes.
  const hidden = !chatPanelShown(state);
  const canSend = canSendChat(state);

  // Going means letting the keyboard go with it. The mode is shared with the
  // screen, and a panel that unmounted while holding it would leave the room in
  // a state where the board refuses keystrokes and nothing on screen takes them
  // — on a phone it would also leave the shell's drawer open, since `chatOpen`
  // there is partly `keyboard.mode === 'chat'`.
  //
  // Destructured rather than read off the prop inside the effect: the api object
  // is rebuilt every render while `releaseChat` itself is a stable callback, so
  // depending on the whole of it would re-run this on every pass. It settles in
  // one either way — `setMode('answer')` on a mode that is already 'answer'
  // bails out inside React — but the narrow dependency is the honest one.
  const { releaseChat } = keyboard;
  useEffect(() => {
    if (hidden) releaseChat();
  }, [hidden, releaseChat]);

  const teamColors = useMemo(() => {
    const map = new Map<string, string>();
    for (const team of state.teams) map.set(team.id, team.color);
    return map;
  }, [state.teams]);

  const authors = useMemo(() => {
    const map = new Map<string, PublicPlayer>();
    for (const player of state.players) map.set(player.id, player);
    return map;
  }, [state.players]);

  // Teams own colour outright: while teams are on, a player reads as their team
  // rather than as themselves.
  const colorOf = (message: ChatMessage): string | null => {
    const author = message.authorId === null ? undefined : authors.get(message.authorId);
    if (author === undefined) return null;
    if (state.teams.length > 0 && author.teamId !== null) {
      return teamColors.get(author.teamId) ?? author.color;
    }
    return author.color;
  };

  const shown = state.chat.filter((message) => readableBy(message, yourTeamId));

  // The host loses the people list the moment they stop being the host, rather
  // than sitting in front of a roster of menus that would all be refused.
  const people = view === 'people' && state.youAreHost;

  useEffect(() => {
    const list = listRef.current;
    if (list !== null && pinnedRef.current) list.scrollTop = list.scrollHeight;
  }, [shown.length]);

  // Escape is handled by the hook, which only knows about the mode. The field
  // has to be let go of here, or the mode says 'answer' while the keystrokes
  // keep landing in chat.
  useEffect(() => {
    const input = inputRef.current;
    if (input === null) return;
    if (focused) input.focus();
    else if (document.activeElement === input) input.blur();
  }, [focused]);

  // After every hook, never before one: the panel is drawn or it is not, and
  // which of those it is changes while the room is looking at it.
  //
  // The room shell hides the drawer and the header button on the same reading,
  // so this is the second half of one decision rather than the whole of it —
  // but it is the half that cannot be forgotten, because a panel that kept
  // drawing itself inside a hidden drawer would still be holding the keyboard.
  // Nothing else in here is chat: the game log is `state.log` and is not drawn
  // in this panel at all.
  if (hidden) return null;

  // Derived, never stored: losing your team mid-draft has to point the next line
  // at everyone rather than at a team you are no longer on.
  const channel: ChatChannel = onTeam ? target : 'global';
  const yourTeamColor = yourTeamId === null ? null : teamColors.get(yourTeamId) ?? null;
  const toTeam = channel === 'team' && yourTeamColor !== null;

  // A prefix moves the control and leaves the field, so what you see is where
  // the line is going. The server parses prefixes too; consuming it here means
  // it is parsed once, by the half the player can see.
  const onDraftChange = (next: string) => {
    const found = prefixed(next, onTeam);
    if (found === null) {
      setDraft(next);
      return;
    }
    setTarget(found.channel);
    setDraft(found.body);
  };

  const submit = () => {
    const found = prefixed(draft, onTeam);
    // Normalised on the way out as well as on the way in, so the body that is
    // stored, censored and broadcast is the body every client shows. A paste is
    // the only way a newline reaches a single-line field; see `singleLine`.
    const body = singleLine(found?.body ?? draft);
    if (body.length === 0) return;
    if (found !== null) setTarget(found.channel);
    send({ type: 'chat', channel: found?.channel ?? channel, body });
    setDraft('');
  };

  return (
    <section
      aria-label="Chat"
      data-chat-focused={focused ? 'true' : 'false'}
      // Keystrokes that land in here do not leave here.
      //
      // The screens bind their game keys on `window` and gate them on
      // `keyboard.mode`, which is the arbitration that matters and is unchanged.
      // This is the second gate, and it exists because the panel now lives in
      // the room shell while each screen still holds its own instance of the
      // hook: pressing slash moves both, but clicking into the field with a
      // mouse moves only this one, and without this the letters of a chat
      // message would also be typed into the answer. Escape is the exception —
      // it releases chat from anywhere, and the hook is listening for it up on
      // the window.
      onKeyDown={(event) => {
        if (event.key !== 'Escape') event.stopPropagation();
      }}
      className={`flex min-h-0 flex-col overflow-hidden rounded-frame border border-line bg-bg ${className ?? ''}`}
    >
      {/* The host's two headings sit exactly where the single label sat, at the
          same size and in the same box, so the panel's geometry does not depend
          on who is reading it. See `ViewTab` for how the tap target is paid
          for. */}
      <div className="flex shrink-0 items-center gap-1 border-b border-line px-[18px] py-4">
        {state.youAreHost ? (
          // `-ml-2` cancels the first tab's own padding, so `chat` starts on the
          // same 18px as the label it replaces rather than eight pixels in.
          <div className="-ml-2 flex items-center gap-1">
            <ViewTab label="chat" on={!people} onSelect={() => setView('chat')} />
            <ViewTab label="people" on={people} onSelect={() => setView('people')} />
          </div>
        ) : (
          <span className="label-mono">chat</span>
        )}
      </div>

      {/* `basis` plus a scroller's own zero minimum size is what fixes the
          height: the list takes 260px where nothing constrains it, absorbs the
          slack where a screen does constrain it, and in neither case is its size
          a function of how many messages are in it. */}
      {people ? (
        <RoomRoster state={state} send={send} />
      ) : (
        <ol
          ref={listRef}
          onScroll={(event) => {
            const list = event.currentTarget;
            pinnedRef.current = list.scrollHeight - list.scrollTop - list.clientHeight < 32;
          }}
          className="flex min-h-0 shrink grow basis-[260px] flex-col gap-1 overflow-y-auto px-2 py-2 [scrollbar-gutter:stable]"
        >
          {shown.map((message) => (
            <ChatMessageRow
              key={message.id}
              message={message}
              authorColor={colorOf(message)}
              teamColor={message.teamId === null ? null : teamColors.get(message.teamId) ?? null}
              // The host's four powers over a line, and only on a line there is
              // something to do about. A system line is the room's own record of
              // what happened to it — the engine refuses a delete on one too, so
              // this is the visible half of a rule rather than the whole of it.
              //
              // The author is looked up rather than assumed: half of these
              // powers need a seat and half of them do not, and the case this
              // control exists for is somebody who has already gone. See
              // `ChatModerationMenu`.
              control={
                state.youAreHost && !message.system ? (
                  <ChatModerationMenu
                    message={message}
                    author={
                      message.authorId === null ? null : authors.get(message.authorId) ?? null
                    }
                    send={send}
                    open={openMenu === message.id}
                    onOpenChange={(next) => setOpenMenu(next ? message.id : null)}
                  />
                ) : undefined
              }
            />
          ))}
        </ol>
      )}

      <div data-testid="chat-composer" className="shrink-0 border-t border-line px-[18px] py-[14px]">
        {muted ? (
          <p className="flex min-h-[44px] items-center text-[13px] leading-[1.45] text-ink-muted">
            You are muted.
          </p>
        ) : !canSend ? (
          // `chatMode: 'players'`. The bench reads and does not write, and the
          // composer is gone rather than present and refusing — a field that
          // takes a sentence and then rejects it costs the sentence, and a
          // spectator watching a round has nowhere else to put it.
          //
          // Its own sentence, not the muted one: being muted is about this
          // person and this says nothing about them at all. Same height as every
          // other composer state, because nothing in this panel may resize.
          <p className="flex min-h-[44px] items-center text-[13px] leading-[1.45] text-ink-muted">
            Only players can chat in this room.
          </p>
        ) : parked && !focused ? (
          <button
            type="button"
            onClick={keyboard.takeChat}
            className="flex min-h-[44px] w-full items-center gap-[10px] rounded-tile border border-dashed border-line bg-bg-alt px-[14px] text-left"
          >
            <span className="text-[13px] text-ink-dim">press slash to focus</span>
            <span className="ml-auto grid h-8 w-8 shrink-0 place-items-center rounded-chip bg-raised font-mono text-[13px] font-semibold text-ink-muted">
              /
            </span>
          </button>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
            data-chat-target={channel}
            // Focus moves the border one token, not one hue. Lighting the whole
            // panel up was the loudest thing on the screen.
            //
            // Aiming at the team changes the field itself, in the same colour
            // and the same rail the team's lines carry in the stream: what you
            // are typing into looks like what it is about to become. The mix is
            // 10% rather than the stream's 16% because this surface starts at
            // `surface` rather than `bg`, and the placeholder on it must stay
            // inside what `ink-dim` was solved for.
            className={`flex min-h-[44px] items-center gap-[10px] rounded-tile border px-[14px] ${
              focused ? 'border-line-strong' : 'border-line'
            } ${toTeam ? '' : 'bg-surface'}`}
            style={
              toTeam
                ? {
                    backgroundColor: `color-mix(in oklab, ${yourTeamColor} 10%, var(--c-surface))`,
                    boxShadow: `inset 3px 0 0 ${yourTeamColor}`,
                  }
                : undefined
            }
          >
            {onTeam ? (
              <div
                role="radiogroup"
                aria-label="Send to"
                className="flex shrink-0 items-center gap-[3px] rounded-chip bg-bg-alt p-[3px]"
              >
                {(
                  [
                    { value: 'global', label: 'all' },
                    { value: 'team', label: 'team' },
                  ] as const
                ).map((option) => {
                  const selected = channel === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setTarget(option.value)}
                      // Fixed widths and one font weight in both states: a
                      // control that resized as it was switched would drag the
                      // field it sits next to.
                      className={`grid h-[26px] shrink-0 place-items-center rounded-chip font-mono text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors duration-150 ease-out ${
                        option.value === 'team' ? 'w-[42px]' : 'w-[34px]'
                      } ${selected ? 'bg-line-strong text-ink-on-fill' : 'text-ink-dim'}`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            ) : null}
            <input
              ref={inputRef}
              value={draft}
              onChange={(event) => onDraftChange(event.target.value)}
              onFocus={keyboard.takeChat}
              onBlur={keyboard.releaseChat}
              maxLength={CHAT_MESSAGE_MAX}
              aria-label={toTeam ? 'Message your team' : 'Message everyone'}
              placeholder={toTeam ? 'Message your team' : 'Message everyone'}
              // 16px, never smaller. iOS zooms the layout viewport into any
              // field under 16 and never zooms back out, so tapping the chat box
              // mid-round left the board magnified behind the drawer — the one
              // complaint that started this pass.
              //
              // The line height is pinned rather than left to `normal` so the
              // two extra pixels of type do not become two extra pixels of
              // composer: nothing in this panel may change size, and the
              // composer is the half of it that a round is watching.
              className="min-w-0 flex-1 bg-transparent py-3 text-[16px] leading-[17px] font-medium text-ink outline-none placeholder:text-ink-dim"
            />
            <button
              type="submit"
              aria-label="Send"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-chip bg-raised text-ink-muted"
            >
              <Icon icon={EnterKeyIcon} size={15} />
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
